/**
 * MappingCreateDialog — define a new Mapping (signature + strategy).
 *
 * Editing existing mappings (description, tags, signature when not
 * locked) happens on the detail page, not in this dialog. That keeps
 * this form focused on the "declare a function" step, which is
 * irreversible enough to want its own confirm.
 */
import { useMutation } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  mappingsApi,
  STRATEGY_META,
  type MappingField,
  type MappingFieldType,
  type MappingSummary,
  type StrategyKind,
} from "@/lib/api-mappings";
import { uid } from "@/lib/uid";

const FIELD_TYPES: MappingFieldType[] = ["string", "integer", "number", "boolean"];
const NAME_RE = /^[a-z][a-z0-9_]*$/;

export interface MappingCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (mapping: MappingSummary) => void;
}

interface FieldDraft extends MappingField {
  // Local-only id for React key stability while editing.
  _key: string;
}

function emptyField(required: boolean): FieldDraft {
  return {
    _key: uid("field"),
    name: "",
    type: "string",
    required,
    description: null,
  };
}

export function MappingCreateDialog({
  open,
  onOpenChange,
  onCreated,
}: MappingCreateDialogProps) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [strategy, setStrategy] = React.useState<StrategyKind>("manual_table");
  const [inputs, setInputs] = React.useState<FieldDraft[]>([
    { ...emptyField(true), name: "key" },
  ]);
  const [outputs, setOutputs] = React.useState<FieldDraft[]>([
    { ...emptyField(true), name: "value" },
  ]);
  const [error, setError] = React.useState<string | null>(null);

  // Reset on close — next open should start clean.
  React.useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
      setStrategy("manual_table");
      setInputs([{ ...emptyField(true), name: "key" }]);
      setOutputs([{ ...emptyField(true), name: "value" }]);
      setError(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      mappingsApi.create({
        name,
        description: description || null,
        inputs: inputs.map(({ _key, ...rest }) => rest),
        outputs: outputs.map(({ _key, ...rest }) => rest),
        strategy_kind: strategy,
        strategy_config: {},
      }),
    onSuccess: (m) => {
      onOpenChange(false);
      onCreated?.(m);
    },
    onError: (err: Error) => setError(err.message),
  });

  function validate(): string | null {
    if (!NAME_RE.test(name)) {
      return "Name must be lowercase, start with a letter, only [a-z0-9_].";
    }
    if (inputs.length === 0) return "At least one input field is required.";
    if (outputs.length === 0) return "At least one output field is required.";
    const allFields = [...inputs, ...outputs];
    for (const f of allFields) {
      if (!NAME_RE.test(f.name)) {
        return `Field name "${f.name}" is invalid — use [a-z0-9_], start with a letter.`;
      }
    }
    const inNames = new Set(inputs.map((f) => f.name));
    if (inNames.size !== inputs.length) return "Duplicate input field names.";
    const outNames = new Set(outputs.map((f) => f.name));
    if (outNames.size !== outputs.length) return "Duplicate output field names.";
    // Inputs and outputs can share names on purpose — the two sides
    // live in separate scopes at runtime ("normalise in place" is a
    // legitimate pattern, e.g. input.code → output.code canonicalised).
    return null;
  }

  function handleSubmit() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    createMutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New mapping</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="m_name">Name</Label>
              <Input
                id="m_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="customer_fluxo_to_canonical"
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-muted-foreground">
                Lowercase, [a-z0-9_], start with a letter. Used as the
                stable handle when workflows call this mapping.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="m_strategy">Strategy</Label>
              <select
                id="m_strategy"
                value={strategy}
                onChange={(e) => setStrategy(e.target.value as StrategyKind)}
                className="h-9 w-full rounded border border-input bg-background px-2 text-sm"
              >
                {(Object.keys(STRATEGY_META) as StrategyKind[]).map((k) => (
                  <option key={k} value={k}>
                    {STRATEGY_META[k].label}
                    {!STRATEGY_META[k].available ? " (resolver pending)" : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                {STRATEGY_META[strategy].description}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="m_desc">Description</Label>
            <textarea
              id="m_desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this mapping do? When should a workflow call it?"
              rows={2}
              className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
            />
          </div>

          <FieldsEditor
            title="Inputs"
            help="Fields the caller MUST pass when invoking this mapping. Each one becomes a column in the lookup table."
            fields={inputs}
            onChange={setInputs}
            defaultRequired
          />

          <FieldsEditor
            title="Outputs"
            help="Fields the mapping returns. Marked nullable means a row can leave them empty."
            fields={outputs}
            onChange={setOutputs}
            defaultRequired
          />

          {strategy === "manual_table" && (
            <p className="rounded border border-border/60 bg-muted/30 p-2 text-xs text-muted-foreground">
              After creating this mapping you'll add rows on the detail
              page — each row is one fully-bound (inputs → outputs)
              tuple. The composite of all inputs is a unique key.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Create mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Field editor — reused for inputs + outputs
// ──────────────────────────────────────────────────────────────────────

interface FieldsEditorProps {
  title: string;
  help: string;
  fields: FieldDraft[];
  onChange: (next: FieldDraft[]) => void;
  defaultRequired: boolean;
}

function FieldsEditor({
  title,
  help,
  fields,
  onChange,
  defaultRequired,
}: FieldsEditorProps) {
  function update(idx: number, patch: Partial<FieldDraft>) {
    onChange(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }
  function remove(idx: number) {
    onChange(fields.filter((_, i) => i !== idx));
  }
  function add() {
    onChange([...fields, emptyField(defaultRequired)]);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <Label>{title}</Label>
        <Badge tone="neutral" className="text-[10px]">{fields.length}</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground">{help}</p>
      <div className="rounded border border-border/60">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-1 text-left w-1/3">Name</th>
              <th className="px-2 py-1 text-left w-28">Type</th>
              <th className="px-2 py-1 text-center w-24">Required</th>
              <th className="px-2 py-1 text-left">Description</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <tr key={f._key} className="border-t border-border/60">
                <td className="px-2 py-1">
                  <Input
                    value={f.name}
                    onChange={(e) => update(i, { name: e.target.value })}
                    placeholder="field_name"
                    className="h-7 font-mono text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={f.type}
                    onChange={(e) =>
                      update(i, { type: e.target.value as MappingFieldType })
                    }
                    className="h-7 w-full rounded border border-input bg-background px-1 text-xs"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1 text-center">
                  <input
                    type="checkbox"
                    checked={f.required}
                    onChange={(e) => update(i, { required: e.target.checked })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Input
                    value={f.description ?? ""}
                    onChange={(e) =>
                      update(i, { description: e.target.value || null })
                    }
                    placeholder="optional"
                    className="h-7 text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    disabled={fields.length === 1}
                    className="text-muted-foreground hover:text-danger disabled:opacity-30"
                    title="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={add}
          className="flex w-full items-center justify-center gap-1 border-t border-border/60 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" /> Add field
        </button>
      </div>
    </div>
  );
}
